import { supabase } from '@/lib/supabase';
import { pushNotification } from '@/lib/notifications';

// ─────────────────────────────────────────────────────────────
// MISSIONS — marketplace interne de campagnes de visibilité.
//
// Un commerce publie une mission (« partagez cette vidéo avec cette
// description, 20 DHS / 1000 vues »). Les autres commerces et les
// comptes clients voient la campagne dans la marketplace, postulent
// (liens réseaux sociaux + nombre d'abonnés) puis, s'ils sont
// retenus, publient le contenu et déclarent le lien de publication.
// Le propriétaire valide les vues et le montant dû est calculé.
// ─────────────────────────────────────────────────────────────

export interface MissionMedia {
  url: string;
  type: string;
}

export interface Mission {
  id: number;
  user_id: number;
  auteur_nom: string | null;
  titre: string;
  description: string | null;
  plateformes: string[] | null;
  type_contenu: string | null;
  media: MissionMedia[] | null;
  lien_contenu: string | null;
  tarif_1000_vues: number | null;
  monaie: string | null;
  vues_cibles: number | null;
  ville: string | null;
  pays: string | null;
  musique_autorisee: boolean | null;
  date_limite: string | null;
  statut: string;
  created_at: string;
  updated_at: string;
}

/** Statuts de candidature. */
export type CandidatureStatut =
  | 'en_attente'
  | 'acceptee'
  | 'refusee'
  | 'livree'
  | 'validee'
  | 'rejetee';

export interface MissionCandidature {
  id: number;
  mission_id: number;
  user_id: number;
  auteur_nom: string | null;
  liens: Record<string, string> | null;
  abonnes: Record<string, number | string> | null;
  message: string | null;
  statut: string;
  lien_publication: string | null;
  vues_declarees: number | null;
  vues_validees: number | null;
  montant_du: number | null;
  created_at: string;
  updated_at: string;
}

export interface MissionInput {
  titre: string;
  description: string;
  plateformes: string[];
  typeContenu: string;
  media: MissionMedia[];
  lienContenu: string;
  tarif1000Vues: number;
  monaie: string;
  vuesCibles: number;
  ville: string;
  pays: string;
  musiqueAutorisee: boolean;
  dateLimite: string;
}

export interface PostulerInput {
  liens: Record<string, string>;
  abonnes: Record<string, number>;
  message: string;
}

export const MISSION_PLATFORMS = [
  { key: 'tiktok', label: 'TikTok', icon: 'ri-tiktok-line' },
  { key: 'instagram', label: 'Instagram', icon: 'ri-instagram-line' },
  { key: 'facebook', label: 'Facebook', icon: 'ri-facebook-circle-line' },
  { key: 'youtube', label: 'YouTube', icon: 'ri-youtube-line' },
  { key: 'x', label: 'X (Twitter)', icon: 'ri-twitter-x-line' },
];

export const MISSION_CURRENCIES = ['MAD', 'EUR', 'USD', 'XOF', 'XAF'];

export const MISSION_STATUT_META: Record<string, { label: string; className: string }> = {
  ouverte: { label: 'Ouverte', className: 'bg-accent-100 text-accent-900' },
  fermee: { label: 'Sélection en cours', className: 'bg-secondary-100 text-secondary-900' },
  terminee: { label: 'Terminée', className: 'bg-background-200/70 text-foreground-600' },
};

export const CANDIDATURE_STATUT_META: Record<string, { label: string; className: string }> = {
  en_attente: { label: 'En attente', className: 'bg-secondary-100 text-secondary-900' },
  acceptee: { label: 'Retenu — à publier', className: 'bg-accent-100 text-accent-900' },
  refusee: { label: 'Non retenu', className: 'bg-background-200/70 text-foreground-600' },
  livree: { label: 'Publication en vérification', className: 'bg-primary-100 text-primary-700' },
  validee: { label: 'Validée — payée', className: 'bg-accent-100 text-accent-900' },
  rejetee: { label: 'Publication rejetée', className: 'bg-red-50 text-red-600' },
};

const SELECT_MISSION =
  'id, user_id, auteur_nom, titre, description, plateformes, type_contenu, media, lien_contenu, tarif_1000_vues, monaie, vues_cibles, ville, pays, musique_autorisee, date_limite, statut, created_at, updated_at';

const SELECT_CANDIDATURE =
  'id, mission_id, user_id, auteur_nom, liens, abonnes, message, statut, lien_publication, vues_declarees, vues_validees, montant_du, created_at, updated_at';

/** Montant dû pour un nombre de vues validées. */
export function computeMissionPayout(
  tarif1000Vues: number | null,
  vues: number | null,
): number {
  const tarif = Number(tarif1000Vues) || 0;
  const v = Number(vues) || 0;
  if (tarif <= 0 || v <= 0) return 0;
  return Math.round((v / 1000) * tarif * 100) / 100;
}

export function missionAbonnesTotal(abonnes: Record<string, number | string> | null): number {
  if (!abonnes) return 0;
  return Object.values(abonnes).reduce((sum, value) => sum + (Number(value) || 0), 0);
}

export async function fetchMissions(): Promise<Mission[]> {
  const { data, error } = await supabase
    .from('missions')
    .select(SELECT_MISSION)
    .order('created_at', { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data || []) as Mission[];
}

export async function fetchMissionCandidatures(missionIds: number[]): Promise<MissionCandidature[]> {
  if (missionIds.length === 0) return [];
  const { data, error } = await supabase
    .from('mission_candidatures')
    .select(SELECT_CANDIDATURE)
    .in('mission_id', missionIds)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []) as MissionCandidature[];
}

export async function createMission(
  userId: number,
  auteurNom: string,
  input: MissionInput,
): Promise<Mission> {
  const { data, error } = await supabase
    .from('missions')
    .insert({
      user_id: userId,
      auteur_nom: auteurNom,
      titre: input.titre.trim(),
      description: input.description.trim(),
      plateformes: input.plateformes,
      type_contenu: input.typeContenu,
      media: input.media,
      lien_contenu: input.lienContenu.trim() || null,
      tarif_1000_vues: input.tarif1000Vues,
      monaie: input.monaie,
      vues_cibles: input.vuesCibles,
      ville: input.ville.trim() || null,
      pays: input.pays.trim() || null,
      musique_autorisee: input.musiqueAutorisee,
      date_limite: input.dateLimite || null,
      statut: 'ouverte',
    })
    .select(SELECT_MISSION)
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('La mission n’a pas pu être créée.');
  return data as Mission;
}

export async function updateMissionStatut(id: number, statut: string): Promise<void> {
  const { error } = await supabase
    .from('missions')
    .update({ statut, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

/** Diffusion de la campagne à la communauté (cloche + push). */
export async function annoncerMission(mission: Mission): Promise<{ notified: number }> {
  let notified = 0;
  try {
    const { data } = await supabase
      .from('users')
      .select('id')
      .neq('id', mission.user_id)
      .limit(500);
    const montant = Number(mission.tarif_1000_vues) || 0;
    const resume = [
      montant > 0 ? `${montant.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${mission.monaie || 'MAD'} / 1000 vues` : '',
      mission.ville ? `Zone : ${mission.ville}` : '',
    ].filter(Boolean).join(' · ');
    for (const row of (data || []) as { id: number }[]) {
      await pushNotification({
        userId: Number(row.id),
        type: 'mission',
        publicationId: mission.id,
        message: `Nouvelle mission : ${mission.titre}. ${resume}`.slice(0, 240),
      });
      notified += 1;
    }
  } catch {
    /* best-effort */
  }
  return { notified };
}

/**
 * Candidature : liens réseaux sociaux + nombre d'abonnés.
 * Les liens saisis sont aussi enregistrés dans le profil (best-effort)
 * pour être pré-remplis la prochaine fois.
 */
export async function postulerMission(
  mission: Mission,
  user: { id: number; nom: string; email?: string | null },
  input: PostulerInput,
): Promise<MissionCandidature> {
  const { data, error } = await supabase
    .from('mission_candidatures')
    .upsert(
      {
        mission_id: mission.id,
        user_id: user.id,
        auteur_nom: user.nom,
        liens: input.liens,
        abonnes: input.abonnes,
        message: input.message.trim() || null,
        statut: 'en_attente',
      },
      { onConflict: 'mission_id,user_id' },
    )
    .select(SELECT_CANDIDATURE)
    .maybeSingle();
  if (error) throw error;

  // Mémorise les liens dans le profil du candidat.
  try {
    const patch: Record<string, string> = {};
    const map: Record<string, string> = {
      facebook: 'lienfacebook',
      instagram: 'lieninstagram',
      tiktok: 'lientiktok',
      x: 'lientwitter',
      youtube: 'lienyoutube',
    };
    Object.entries(map).forEach(([key, column]) => {
      const value = (input.liens[key] || '').trim();
      if (value) patch[column] = value;
    });
    if (Object.keys(patch).length > 0) {
      await supabase.from('users').update(patch).eq('id', user.id);
    }
  } catch {
    /* best-effort */
  }

  await pushNotification({
    userId: mission.user_id,
    type: 'mission',
    publicationId: mission.id,
    message: `${user.nom} a postulé à « ${mission.titre} » (${missionAbonnesTotal(input.abonnes).toLocaleString('fr-FR')} abonnés au total).`,
  });

  if (!data) throw new Error('Candidature non enregistrée.');
  return data as MissionCandidature;
}

/** Gain d'un créateur pour une mission (candidature + campagne). */
export interface CreatorEarning {
  id: number;
  missionId: number;
  missionTitre: string;
  monaie: string;
  statut: string;
  vuesDeclarees: number | null;
  vuesValidees: number | null;
  montantDu: number;
  lienPublication: string | null;
  date: string;
}

/**
 * Gains d'un créateur : toutes ses candidatures, enrichies du titre de la
 * campagne et du montant dû. Les montants validés par le propriétaire de la
 * mission constituent les revenus réellement acquis.
 */
export async function fetchCreatorEarnings(userId: number): Promise<CreatorEarning[]> {
  const { data, error } = await supabase
    .from('mission_candidatures')
    .select(SELECT_CANDIDATURE)
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });
  if (error) throw error;

  const rows = (data || []) as MissionCandidature[];
  if (rows.length === 0) return [];

  const ids = [...new Set(rows.map((r) => Number(r.mission_id)))].filter((id) => !Number.isNaN(id));
  const missionsById = new Map<number, Mission>();
  if (ids.length > 0) {
    const { data: missionsData } = await supabase
      .from('missions')
      .select(SELECT_MISSION)
      .in('id', ids);
    ((missionsData || []) as Mission[]).forEach((m) => missionsById.set(Number(m.id), m));
  }

  return rows.map((row) => {
    const mission = missionsById.get(Number(row.mission_id));
    return {
      id: Number(row.id),
      missionId: Number(row.mission_id),
      missionTitre: mission?.titre || `Mission #${row.mission_id}`,
      monaie: mission?.monaie || 'MAD',
      statut: row.statut,
      vuesDeclarees: row.vues_declarees,
      vuesValidees: row.vues_validees,
      montantDu: Number(row.montant_du) || 0,
      lienPublication: row.lien_publication,
      date: row.updated_at || row.created_at,
    };
  });
}

async function updateCandidature(id: number, patch: Record<string, unknown>): Promise<void> {
  const { error } = await supabase
    .from('mission_candidatures')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

/** Le propriétaire retient un candidat : il peut publier le contenu. */
export async function accepterCandidat(
  mission: Mission,
  candidature: MissionCandidature,
): Promise<void> {
  await updateCandidature(candidature.id, { statut: 'acceptee' });
  await pushNotification({
    userId: candidature.user_id,
    type: 'mission',
    publicationId: mission.id,
    message: `Vous êtes retenu pour « ${mission.titre} ». Publiez le contenu puis ajoutez le lien de votre publication.`,
  });
}

export async function refuserCandidat(
  mission: Mission,
  candidature: MissionCandidature,
): Promise<void> {
  await updateCandidature(candidature.id, { statut: 'refusee' });
  await pushNotification({
    userId: candidature.user_id,
    type: 'mission',
    publicationId: mission.id,
    message: `Votre candidature pour « ${mission.titre} » n’a pas été retenue.`,
  });
}

/** Le candidat retenu déclare sa publication (lien + vues constatées). */
export async function soumettreLivraison(
  mission: Mission,
  candidature: MissionCandidature,
  lienPublication: string,
  vuesDeclarees: number,
): Promise<void> {
  await updateCandidature(candidature.id, {
    lien_publication: lienPublication.trim(),
    vues_declarees: vuesDeclarees,
    statut: 'livree',
  });
  await pushNotification({
    userId: mission.user_id,
    type: 'mission',
    publicationId: mission.id,
    message: `${candidature.auteur_nom || 'Un candidat'} a publié son contenu pour « ${mission.titre} » (${vuesDeclarees.toLocaleString('fr-FR')} vues déclarées). À vérifier.`,
  });
}

/** Le propriétaire valide une publication et fixe les vues retenues. */
export async function validerLivraison(
  mission: Mission,
  candidature: MissionCandidature,
  vuesValidees: number,
): Promise<number> {
  const montant = computeMissionPayout(mission.tarif_1000_vues, vuesValidees);
  await updateCandidature(candidature.id, {
    vues_validees: vuesValidees,
    montant_du: montant,
    statut: 'validee',
  });
  await pushNotification({
    userId: candidature.user_id,
    type: 'mission',
    publicationId: mission.id,
    message: `Votre publication pour « ${mission.titre} » est validée : ${vuesValidees.toLocaleString('fr-FR')} vues → ${montant.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${mission.monaie || 'MAD'}.`,
  });
  return montant;
}

export async function rejeterLivraison(
  mission: Mission,
  candidature: MissionCandidature,
  motif: string,
): Promise<void> {
  await updateCandidature(candidature.id, {
    statut: 'rejetee',
    montant_du: 0,
  });
  await pushNotification({
    userId: candidature.user_id,
    type: 'mission',
    publicationId: mission.id,
    message: `Publication refusée pour « ${mission.titre} »${motif ? ` — ${motif}` : ''}. Corrigez puis renvoyez le lien.`,
  });
}